import { ChangeEmitter } from '@/application/ChangeEmitter';
import { TextboxField } from '@/domain/textbox/TextboxField';
import type { I2DEngine } from '@/application/ports/I2DEngine';
import type { IEntityRepository } from '@/application/ports/IEntityRepository';
import type { ISelectionState } from '@/application/ports/ISelectionState';
import type { TSelectionRange } from '@/domain/textbox/TSelectionRange';
import type { TViewTransform } from '@/application/ports/TViewTransform';

// Mirrors engine-side user interactions (drag, in-place edit, marquee) into the repository.
class EngineEventSubscriber {
    private readonly _unsubscribes: (() => void)[];

    constructor(
        private readonly _engine: I2DEngine,
        private readonly _repository: IEntityRepository,
        private readonly _selection: ISelectionState,
        private readonly _changeEmitter: ChangeEmitter,
    ) {
        this._unsubscribes = [
            this._engine.onViewTransformed((transform) => this._onViewTransformed(transform)),
            this._engine.onTextChanged((payload) => this._onTextChanged(payload)),
            this._engine.onSelectionChanged((entityId) => this._onSelectionChanged(entityId)),
            this._engine.onTextSelectionChanged((range) => this._onTextSelectionChanged(range)),
        ];
    }

    dispose(): void {
        this._unsubscribes.forEach((unsubscribe) => unsubscribe());
    }

    private _onViewTransformed(transform: TViewTransform): void {
        const entity = this._repository.get(transform.id);
        if (!entity) {
            return;
        }
        entity.applyViewTransform(transform);
        this._repository.update(entity);
        this._changeEmitter.emit();
    }

    private _onTextChanged(payload: { id: string; text: string }): void {
        const entity = this._repository.get(payload.id);
        if (!(entity instanceof TextboxField)) {
            return;
        }
        entity.setText(payload.text);
        this._repository.update(entity);
        this._changeEmitter.emit();
    }

    private _onSelectionChanged(entityId: string | undefined): void {
        this._selection.setSelectedId(entityId);
    }

    private _onTextSelectionChanged(range: TSelectionRange | undefined): void {
        this._selection.setSelectedTextRange(range);
    }
}

export { EngineEventSubscriber };
